import { api, createQuery } from "helpers/api";
import useLocalStorage from "hooks/useLocalStorage";
import React, {
  useMemo,
  useContext,
  useState,
  useEffect,
} from "react";
import { Avatar, Space, Typography } from "antd";
import dayjs from "dayjs";
import {
  filter_issue_kinds,
  getIssueKind,
} from "helpers/options/issue_kinds";
import {
  filter_issue_statuses,
  getIssueStatusLabel,
} from "helpers/options/issue_statuses";
import formatPhone from "helpers/phone_formatter";
import {
  worker_billing_status_filter_options,
  administration_billing_status_filter_options,
  getAdministratorBillingStatus,
  getWorkerBillingStatus,
} from "helpers/options/billing_statuses";
import { useWorkers } from "context/providers/workers";

import EllipsisText from "components/EllipsisText";
import { useAuth } from "context/providers/auth";

const SettlementContext = React.createContext();

const getColumns = (workers_options) => [
  {
    title: "ID",
    dataIndex: "id",
    sortable: true,
    searchable: true,
    search_method: "eq",
    search_type: "number",
  },
  {
    title: "Nazwa ulicy",
    dataIndex: "client_street",
    sortable: true,
    searchable: true,
    search_method: "matches",
    render: (_, { client_street }) => (
      <EllipsisText text={client_street} limit={50} />
    ),
  },
  {
    title: "Nr budynku",
    dataIndex: "client_building_number",
    sortable: true,
    searchable: true,
    search_method: "matches",
    width: 120,
  },
  {
    title: "Nr mieszkania",
    dataIndex: "client_apartment_number",
    sortable: true,
    searchable: true,
    search_method: "matches",
    width: 130,
  },
  {
    title: "Telefon",
    dataIndex: "client_phone",
    sortable: true,
    searchable: true,
    search_method: "matches",
    search_type: "number",
    render: (_, { client_phone }) =>
      formatPhone(client_phone),
  },
  {
    title: "Data umówienia",
    dataIndex: "service_date",
    sortable: true,
    searchable: true,
    search_method: "date",
    search_component: "date-picker",
    render: (_, { service_date }) =>
      service_date
        ? dayjs(service_date).format("DD-MM-YYYY HH:mm")
        : "-",
  },
  {
    title: "Status",
    dataIndex: "status",
    sortable: true,
    searchable: true,
    search_method: "in",
    search_component: "select",
    options: filter_issue_statuses,
    render: (_, { status }) => getIssueStatusLabel(status),
  },
  {
    title: "Pracownik",
    dataIndex: "worker_id",
    sortable: true,
    searchable: true,
    search_method: "in",
    search_component: "select",
    options: workers_options,
    render: (_, { worker }) =>
      worker?.id ? (
        <Space>
          <Avatar
            style={{ backgroundColor: worker?.color }}
          >
            {worker.initials}
          </Avatar>
          <Typography.Text delete={worker?.is_deleted}>
            <EllipsisText
              text={worker.full_name}
              limit={30}
            />
          </Typography.Text>
        </Space>
      ) : (
        "-"
      ),
  },
  {
    title: "Stan roz. pracownik",
    dataIndex: "worker_billing_status",
    sortable: false,
    searchable: true,
    search_method: "in",
    search_component: "select",
    options: worker_billing_status_filter_options,
    render: (_, { worker_billing_status }) =>
      getWorkerBillingStatus(worker_billing_status),
  },
  {
    title: "Stan roz. administracja",
    dataIndex: "administration_billing_status",
    sortable: false,
    searchable: true,
    search_method: "in",
    search_component: "select",
    options: administration_billing_status_filter_options,
    render: (_, { administration_billing_status }) =>
      getAdministratorBillingStatus(
        administration_billing_status
      ),
  },
  {
    title: "Rodzaj",
    dataIndex: "kind",
    sortable: true,
    searchable: true,
    search_method: "in",
    search_component: "select",
    options: filter_issue_kinds,
    render: (_, { kind }) => getIssueKind(kind),
  },
  {
    title: "Data utworzenia",
    dataIndex: "created_at",
    sortable: true,
    searchable: true,
    search_method: "date-range",
    search_component: "range-picker",
    render: (_, { created_at }) =>
      created_at
        ? dayjs(created_at).format("DD-MM-YYYY HH:mm")
        : "-",
  },
];

function SettlementProvider({ children }) {
  const { workers_list_options } = useWorkers();
  const columns = getColumns(workers_list_options);
  const {
    state: {
      user: { issues_settlement_columns },
    },
    updateUserIssueSettlementColumns,
  } = useAuth();

  const checked_list_options = columns.map(
    ({ dataIndex, title }) => ({
      label: title,
      value: dataIndex,
    })
  );

  const [issue_id, setIssueId] = useLocalStorage(
    "settlement_issue_id",
    null
  );

  const [paginate, setPaginate] = useLocalStorage(
    "settlement_paginate",
    {
      page: 1,
      per_page: 15,
      sort: null,
    }
  );

  const [query, setQuery] = useLocalStorage(
    "settlement_query",
    {}
  );

  const [search_values, setSearchValues] = useLocalStorage(
    "settlement_search_values",
    null
  );

  const handleSetSearchValues = (values) => {
    setSearchValues(values);
    if (
      values?.worker_billing_status === 0 ||
      values?.administration_billing_status === 0
    ) {
      setPaginate({
        ...paginate,
        page: undefined,
        sort: "service_date__asc",
      });
      return;
    }
    if ([undefined, 2].includes(values?.status)) {
      setPaginate({
        ...paginate,
        page: undefined,
        sort: "service_date__desc",
      });
      return;
    }
    if ([0, 1].includes(values?.status)) {
      setPaginate({
        ...paginate,
        page: undefined,
        sort: "service_date__asc",
      });
      return;
    }
    setPaginate({
      ...paginate,
      page: undefined,
    });
  };

  const [has_more_issues, setHasMoreIssues] =
    useState(false);

  const [data, setData] = useState([]);
  const [is_loading, setLoading] = useState(false);
  const [total_count, setTotalCount] = useState(0);

  const getIssues = async () => {
    try {
      setLoading(true);
      const res = await api.get(
        `/issues?${createQuery({
          context: "settlements",
          query,
          paginate: { ...paginate, page: 1 },
        })}`
      );
      setPaginate({
        ...paginate,
        page: res.meta.next_page,
      });
      setTotalCount(res.meta.total_count);
      setHasMoreIssues(res.meta.next_page > 0);
      setData(res.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const getMoreIssues = async () => {
    if (is_loading) {
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(
        `/issues?${createQuery({
          context: "settlement",
          query,
          paginate,
        })}`
      );
      setPaginate({
        ...paginate,
        page: res.meta.next_page,
      });
      setHasMoreIssues(res.meta.next_page > 0);
      setData([...data, ...res.data]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const updateListIssue = ({
    administration_billing_status,
    worker_billing_status,
    status,
    client_building_number,
    client_apartment_number,
    client_phone,
    client_street,
    kind,
    service_date,
  }) => {
    const data_copy = [...data];
    const index = data_copy.indexOf(
      data_copy.find(({ id }) => id === issue_id)
    );
    if (index > -1) {
      Object.assign(data_copy[index], {
        ...data_copy[index],
        administration_billing_status,
        worker_billing_status,
        status,
        client_building_number,
        client_apartment_number,
        client_phone,
        client_street,
        kind,
        service_date,
      });
      setData(data_copy);
    }
  };

  const handleReset = () => {
    setQuery({});
    setIssueId(null);
    setSearchValues({});
    setPaginate({
      ...paginate,
      page: 1,
      per_page: 15,
      sort: undefined,
    });
  };

  useEffect(() => {
    getIssues();
    // eslint-disable-next-line
  }, [query, paginate.sort]);

  const updateCheckedList = async (keys) => {
    try {
      await updateUserIssueSettlementColumns(keys);
    } catch (error) {}
  };

  const value = useMemo(() => {
    return {
      total_count,
      paginate,
      has_more_issues,
      data,
      is_loading,
      issue_id,
      search_values,
      query,
      setHasMoreIssues,
      setData,
      setLoading,
      updateListIssue,
      setIssueId,
      handleSetSearchValues,
      setQuery,
      getMoreIssues,
      setPaginate,
      handleReset,
      columns,
      table_columns: columns.filter((item) =>
        issues_settlement_columns.includes(item.dataIndex)
      ),
      checked_list: issues_settlement_columns,
      updateCheckedList,
      checked_list_options,
    };
    // eslint-disable-next-line
  }, [
    data,
    issue_id,
    search_values,
    query,
    issues_settlement_columns,
    columns,
  ]);

  return (
    <SettlementContext.Provider value={value}>
      {children}
    </SettlementContext.Provider>
  );
}

const useSettlement = () => useContext(SettlementContext);
export { useSettlement };
export default SettlementProvider;
